<template>
  <div class="decont-page">
    <!-- Listă deconturi -->
    <div v-if="!activ">
      <div class="hero">
        <div class="hero-txt">
          <div class="hero-eyebrow">PORTAL HR</div>
          <h1 class="hero-title">Deconturi</h1>
          <p class="hero-sub">Cheltuielile tale de decontat: creează, adaugă documente și trimite spre aprobare.</p>
        </div>
        <v-btn class="btn-nou" prepend-icon="mdi-plus" @click="dialogNou = true">Decont nou</v-btn>
      </div>

      <div class="grid-card">
        <div class="grid-head">
          <v-icon size="18" color="#ff5722" class="mr-2">mdi-cash-multiple</v-icon>
          <span>Deconturile mele</span>
        </div>
        <DxDataGrid
          :data-source="deconturi"
          key-expr="ID"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :hover-state-enabled="true"
          :row-alternation-enabled="false"
          :no-data-text="loading ? 'Se încarcă…' : 'Nu ai niciun decont încă.'"
        >
          <DxColumn data-field="Numar" caption="Număr" :width="130" cell-template="nrCell" />
          <DxColumn data-field="Data" caption="Data" data-type="date" format="dd.MM.yyyy" :width="130" />
          <DxColumn data-field="Explicatie" caption="Explicație" :min-width="220" />
          <DxColumn data-field="Stare" caption="Stare" :width="170" cell-template="stareCell" />
          <DxColumn caption="" :width="150" :allow-filtering="false" :allow-sorting="false" cell-template="actCell" />

          <template #nrCell="{ data }">
            <span class="nr-badge">{{ data.data.Numar || '—' }}</span>
          </template>
          <template #stareCell="{ data }">
            <span class="stare-pill" :style="starePillStyle(data.data.Stare)">
              <span class="dot" :style="{ background: stareColor(data.data.Stare) }" />
              {{ stareLabel(data.data.Stare) }}
            </span>
          </template>
          <template #actCell="{ data }">
            <v-btn size="small" variant="outlined" color="#ff5722" @click="deschide(data.data)">Vizualizează</v-btn>
          </template>

          <DxPaging :page-size="12" />
          <DxSorting mode="single" />
          <DxFilterRow :visible="true" />
        </DxDataGrid>
      </div>
    </div>

    <!-- Detaliu decont -->
    <div v-else>
      <div class="hero">
        <div class="hero-txt">
          <div class="hero-eyebrow">PORTAL HR</div>
          <h1 class="hero-title">Decont {{ activ.Numar }}</h1>
          <p class="hero-sub">{{ activ.Explicatie }}</p>
        </div>
        <div class="hero-actions">
          <span class="stare-pill light" :style="starePillStyle(activ.Stare)">
            <span class="dot" :style="{ background: stareColor(activ.Stare) }" />
            {{ stareLabel(activ.Stare) }}
          </span>
          <v-btn class="btn-back" variant="text" prepend-icon="mdi-arrow-left" @click="inapoi">Înapoi</v-btn>
        </div>
      </div>

      <div class="grid-card mb-4">
        <div class="grid-head">
          <v-icon size="18" color="#ff5722" class="mr-2">mdi-format-list-bulleted</v-icon>
          <span>Documente</span>
          <v-spacer />
          <v-btn v-if="esteDraft" size="small" class="btn-nou" prepend-icon="mdi-plus" @click="dialogLinie = true">Adaugă linie</v-btn>
        </div>

        <div v-if="loadingDetalii" class="pa-6 text-center text-medium-emphasis">Se încarcă…</div>
        <div v-else-if="!linii.length" class="pa-6 text-center text-medium-emphasis">Niciun document adăugat.</div>
        <table v-else class="tbl">
          <thead>
            <tr>
              <th>Tip</th><th>Nr. document</th><th>Data</th><th>Partener</th><th>Total</th><th>Explicație</th><th>Atașamente</th><th v-if="esteDraft"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in linii" :key="l.ID">
              <td>{{ l.TipNume }}</td>
              <td>{{ l.Numar || '—' }}</td>
              <td>{{ fmt(l.DataDoc) }}</td>
              <td>{{ l.PartenerNume || '—' }}</td>
              <td class="txt-right">{{ formatSuma(l.Total) }} {{ l.SimbolValuta }}</td>
              <td>{{ l.Explicatie || '—' }}</td>
              <td>
                <span v-if="!l.Fisiere || !l.Fisiere.length" class="text-muted">—</span>
                <span v-else v-for="f in l.Fisiere" :key="f.FisierID" class="fisier-chip">
                  <v-icon size="14">mdi-paperclip</v-icon>{{ f.Nume }}
                </span>
              </td>
              <td v-if="esteDraft">
                <v-btn icon="mdi-delete-outline" size="small" variant="text" color="red" @click="stergeLinie(l.ID)" />
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="txt-right total-lbl">Total</td>
              <td class="txt-right total-val">{{ formatSuma(totalLinii) }}</td>
              <td :colspan="esteDraft ? 3 : 2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-if="esteDraft" class="d-flex justify-end">
        <v-btn class="btn-nou px-6" :disabled="!linii.length" :loading="trimiterePendinta" @click="trimite">
          <v-icon start>mdi-send</v-icon>Trimite decont
        </v-btn>
      </div>
    </div>

    <!-- Dialog decont nou -->
    <v-dialog v-model="dialogNou" max-width="520" persistent>
      <v-card rounded="xl" class="dlg-card">
        <div class="dlg-head">
          <div class="dlg-ic"><v-icon color="#fff" size="20">mdi-cash-plus</v-icon></div>
          <div>
            <div class="dlg-title">Decont nou</div>
            <div class="dlg-desc">Creează un decont, apoi adaugă documentele.</div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="#fff" size="small" @click="dialogNou = false" />
        </div>
        <v-card-text class="pa-5">
          <v-form v-model="formNouValid" ref="formNou">
            <v-select
              v-model="formNou.ProiectID" :items="proiecte" item-title="Nume" item-value="Id"
              label="Proiect" variant="outlined" density="comfortable" class="mb-4" :loading="loadingLookups" hide-details="auto" clearable />
            <v-textarea
              v-model="formNou.Explicatie" label="Explicație" variant="outlined" density="comfortable" rows="3"
              :rules="[required]" hide-details="auto" />
          </v-form>
          <v-alert v-if="errNou" type="error" variant="tonal" density="compact" class="mt-4">{{ errNou }}</v-alert>
        </v-card-text>
        <v-card-actions class="dlg-foot">
          <v-btn variant="text" @click="dialogNou = false">Anulează</v-btn>
          <v-spacer />
          <v-btn class="btn-nou px-6" :loading="loadingNou" :disabled="!formNouValid" @click="creeazaDecont">
            <v-icon start>mdi-check</v-icon>Creează
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog linie nouă -->
    <v-dialog v-model="dialogLinie" max-width="560" persistent>
      <v-card rounded="xl" class="dlg-card">
        <div class="dlg-head">
          <div class="dlg-ic"><v-icon color="#fff" size="20">mdi-file-document-plus-outline</v-icon></div>
          <div>
            <div class="dlg-title">Document nou</div>
            <div class="dlg-desc">Adaugă un document de decontat.</div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="#fff" size="small" @click="dialogLinie = false" />
        </div>
        <v-card-text class="pa-5">
          <v-form v-model="formLinieValid" ref="formLinie">
            <v-select
              v-model="formLinie.TipID" :items="tipuri" item-title="Nume" item-value="Id"
              label="Tip document" variant="outlined" density="comfortable" class="mb-4" :rules="[required]" :loading="loadingLookups" hide-details="auto" />
            <v-text-field
              v-model="formLinie.Numar" label="Număr document" variant="outlined" density="comfortable" class="mb-4" hide-details="auto" />

            <v-checkbox v-model="formLinie.NuExistaPartener" label="Nu există partener" density="comfortable" hide-details class="mb-2" />
            <v-select
              v-if="!formLinie.NuExistaPartener"
              v-model="formLinie.PartenerID" :items="parteneri" item-title="Nume" item-value="Id"
              label="Partener" variant="outlined" density="comfortable" class="mb-4" :loading="loadingLookups" hide-details="auto" clearable />
            <v-text-field
              v-else
              v-model="formLinie.PartenerNume" label="Nume partener" variant="outlined" density="comfortable" class="mb-4" hide-details="auto" />

            <div class="d-flex ga-3 mb-4">
              <v-text-field
                v-model.number="formLinie.Total" label="Total" type="number" variant="outlined" density="comfortable"
                :rules="[required]" hide-details="auto" class="flex-1-1" />
              <v-select
                v-model="formLinie.ValutaID" :items="valute" item-title="Simbol" item-value="Id"
                label="Valută" variant="outlined" density="comfortable" :loading="loadingLookups" hide-details="auto" style="max-width: 130px" />
            </div>

            <v-textarea
              v-model="formLinie.Explicatie" label="Explicație" variant="outlined" density="comfortable" rows="2" class="mb-4" hide-details="auto" />

            <v-file-input
              v-model="formLinie.files" label="Documente atașate" variant="outlined" density="comfortable"
              multiple accept=".pdf,.jpg,.jpeg,.png" prepend-icon="" prepend-inner-icon="mdi-paperclip" hide-details="auto" />
          </v-form>
          <v-alert v-if="errLinie" type="error" variant="tonal" density="compact" class="mt-4">{{ errLinie }}</v-alert>
        </v-card-text>
        <v-card-actions class="dlg-foot">
          <v-btn variant="text" @click="dialogLinie = false">Anulează</v-btn>
          <v-spacer />
          <v-btn class="btn-nou px-6" :loading="loadingLinie" :disabled="!formLinieValid" @click="adaugaLinie">
            <v-icon start>mdi-plus</v-icon>Adaugă linie
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting } from 'devextreme-vue/data-grid'
import notify from 'devextreme/ui/notify'
import PORTAL_Decont from '@/services/Portal/PORTAL_Decont'

export default defineComponent({
  name: 'Decont',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting },
  data() {
    return {
      loading: false,
      loadingDetalii: false,
      loadingLookups: false,
      loadingNou: false,
      loadingLinie: false,
      trimiterePendinta: false,
      deconturi: [] as any[],
      activ: null as any,
      linii: [] as any[],
      dialogNou: false,
      dialogLinie: false,
      formNouValid: false,
      formLinieValid: false,
      errNou: '',
      errLinie: '',
      tipuri: [] as any[],
      valute: [] as any[],
      parteneri: [] as any[],
      proiecte: [] as any[],
      formNou: { ProiectID: null as string | null, Explicatie: '' },
      formLinie: this.defaultFormLinie(),
    }
  },
  computed: {
    esteDraft(): boolean {
      return this.activ?.Stare === 'draft';
    },
    totalLinii(): number {
      return this.linii.reduce((s, l) => s + (Number(l.Total) || 0), 0);
    }
  },
  async mounted() {
    await Promise.all([this.incarcaDeconturi(), this.incarcaLookups()]);
  },
  methods: {
    defaultFormLinie() {
      return {
        TipID: '', Numar: '', PartenerID: null as string | null, PartenerNume: '',
        NuExistaPartener: false, Total: null as number | null, ValutaID: null as string | null,
        Explicatie: '', files: [] as File[],
      };
    },
    required(v: any) { return (v !== null && v !== undefined && v !== '') || 'Câmp obligatoriu'; },
    fmt(d: any) { return d ? new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'; },
    formatSuma(v: number) { return (Number(v) || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    stareColor(stare: string) {
      const map: Record<string, string> = { draft: '#94a3b8', spreAprobare: '#f59e0b', aprobat: '#16a34a', respins: '#dc2626' };
      return map[stare] ?? '#94a3b8';
    },
    starePillStyle(stare: string) {
      const soft: Record<string, string> = { draft: '#f1f5f9', spreAprobare: '#fff7ed', aprobat: '#ecfdf5', respins: '#fef2f2' };
      return { background: soft[stare] ?? '#f1f5f9', color: this.stareColor(stare) };
    },
    stareLabel(stare: string) {
      const map: Record<string, string> = { draft: 'Ciornă', spreAprobare: 'Spre aprobare', aprobat: 'Aprobat', respins: 'Respins' };
      return map[stare] ?? stare;
    },
    async incarcaDeconturi() {
      this.loading = true;
      try {
        const resp = await PORTAL_Decont.getMele();
        this.deconturi = resp.data ?? [];
      } catch {
        this.deconturi = [];
      } finally {
        this.loading = false;
      }
    },
    async incarcaLookups() {
      this.loadingLookups = true;
      try {
        const [t, v, p, pr] = await Promise.all([
          PORTAL_Decont.getTipuri().catch(() => null),
          PORTAL_Decont.getValute().catch(() => null),
          PORTAL_Decont.getParteneri().catch(() => null),
          PORTAL_Decont.getProiecte().catch(() => null),
        ]);
        this.tipuri = t?.data ?? [];
        this.valute = v?.data ?? [];
        this.parteneri = p?.data ?? [];
        this.proiecte = pr?.data ?? [];
        const ron = this.valute.find((x: any) => x.Simbol === 'RON');
        if (ron) this.formLinie.ValutaID = ron.Id;
      } finally {
        this.loadingLookups = false;
      }
    },
    async deschide(d: any) {
      this.activ = d;
      await this.incarcaDetalii(d.ID);
    },
    async incarcaDetalii(id: string) {
      this.loadingDetalii = true;
      try {
        const [headResp, detResp] = await Promise.all([
          PORTAL_Decont.getById(id),
          PORTAL_Decont.getDetalii(id),
        ]);
        this.activ = headResp.data ?? this.activ;
        this.linii = detResp.data ?? [];
      } catch {
        this.linii = [];
      } finally {
        this.loadingDetalii = false;
      }
    },
    inapoi() {
      this.activ = null;
      this.linii = [];
      this.incarcaDeconturi();
    },
    async creeazaDecont() {
      this.loadingNou = true; this.errNou = '';
      try {
        const resp = await PORTAL_Decont.creeaza(this.formNou);
        this.dialogNou = false;
        const nou = resp.data;
        this.formNou = { ProiectID: null, Explicatie: '' };
        await this.incarcaDeconturi();
        await this.deschide({ ID: nou.ID, Numar: nou.Numar, Stare: 'draft' });
      } catch (e: any) {
        this.errNou = e?.response?.data?.message ?? e?.response?.data ?? 'Eroare la creare.';
      } finally {
        this.loadingNou = false;
      }
    },
    async adaugaLinie() {
      if (!this.activ) return;
      this.loadingLinie = true; this.errLinie = '';
      try {
        await PORTAL_Decont.adaugaLinie(this.activ.ID, this.formLinie as any);
        this.dialogLinie = false;
        const valutaId = this.formLinie.ValutaID;
        this.formLinie = this.defaultFormLinie();
        this.formLinie.ValutaID = valutaId;
        await this.incarcaDetalii(this.activ.ID);
        notify('Document adăugat.', 'success', 2500);
      } catch (e: any) {
        this.errLinie = e?.response?.data?.message ?? e?.response?.data ?? 'Eroare la adăugare.';
      } finally {
        this.loadingLinie = false;
      }
    },
    async stergeLinie(id: string) {
      try {
        await PORTAL_Decont.stergeLinie(id);
        await this.incarcaDetalii(this.activ.ID);
        notify('Document șters.', 'success', 2500);
      } catch {
        notify('Eroare la ștergere.', 'error', 3000);
      }
    },
    async trimite() {
      if (!this.activ) return;
      this.trimiterePendinta = true;
      try {
        await PORTAL_Decont.trimite(this.activ.ID);
        notify('Decont trimis spre aprobare.', 'success', 3000);
        await this.incarcaDetalii(this.activ.ID);
      } catch {
        notify('Eroare la trimitere.', 'error', 3000);
      } finally {
        this.trimiterePendinta = false;
      }
    },
  }
})
</script>

<style scoped>
.decont-page { padding: 16px; width: 100%; }

.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 22px 26px; color: #fff; box-shadow: 0 10px 30px rgba(255,87,34,.28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.hero-actions { display: flex; align-items: center; gap: 12px; }
.btn-nou { background: #fff !important; color: #ff5722 !important; font-weight: 700; border-radius: 22px; text-transform: none; letter-spacing: 0; }
.btn-back { color: #fff !important; font-weight: 700; text-transform: none; }

.grid-card { background: #fff; border: 1px solid #eef1f6; border-radius: 16px; box-shadow: 0 1px 3px rgba(15,23,42,.05); overflow: hidden; }
.grid-head { display: flex; align-items: center; padding: 15px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; font-weight: 700; color: #1e293b; }
.nr-badge { font-family: 'Roboto Mono', monospace; font-weight: 700; font-size: 12.5px; color: #ff5722; background: #fff1ec; padding: 3px 9px; border-radius: 8px; }
.stare-pill { display: inline-flex; align-items: center; gap: 7px; padding: 5px 13px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; }
.stare-pill.light { background: rgba(255,255,255,.22) !important; color: #fff !important; }
.stare-pill.light .dot { background: #fff !important; }
.stare-pill .dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.text-muted { color: #94a3b8; font-style: italic; }

.tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
.tbl th { text-align: left; padding: 10px 16px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: #94a3b8; border-bottom: 1px solid #f1f5f9; }
.tbl td { padding: 10px 16px; border-bottom: 1px solid #f8fafc; color: #334155; }
.tbl tfoot td { border-top: 2px solid #f1f5f9; border-bottom: none; }
.txt-right { text-align: right; }
.total-lbl { font-weight: 700; color: #1e293b; }
.total-val { font-weight: 800; color: #ff5722; font-size: 14px; }
.fisier-chip { display: inline-flex; align-items: center; gap: 4px; background: #fff1ec; color: #ff5722; padding: 2px 8px; border-radius: 8px; font-size: 11px; margin-right: 4px; }

.dlg-card { overflow: hidden; }
.dlg-head { display: flex; align-items: center; gap: 12px; background: linear-gradient(120deg,#ff5722,#ff8a5c); color: #fff; padding: 16px 18px; }
.dlg-ic { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dlg-title { font-size: 16px; font-weight: 800; line-height: 1.15; }
.dlg-desc { font-size: 12px; opacity: .9; margin-top: 2px; }
.dlg-foot { padding: 14px 20px; border-top: 1px solid #eef1f6; }
</style>
